import { computed, ref } from 'vue'
import type { MallProduct } from '@/constants/mallProducts'
import {
  addServerCartItem,
  clearServerCart,
  fetchServerCart,
  mergeServerCart,
  removeServerCartItem,
  updateServerCartItem,
  type ServerCartItem,
} from '@/api/mallCart'
import { calcMallUnitPrice } from '@/utils/mallPricing'
import { toShippingLine } from '@/utils/mallShipping'
import { calcOrderShippingFromLines, normalizeImageShows, normalizeOrderStep, normalizePackQty, normalizeSellUnit, normalizeShippingCharge, normalizeShippingEvery } from '@/utils/mallSell'

const LEGACY_STORAGE_KEY = 'portal.mall.cart'

export interface CartItem {
  productId: string
  qty: number
  optionName?: string
  optionExtra?: number
}

export interface CartLine extends CartItem {
  key: string
  idx: number
  product: MallProduct
  unitPrice: number
  baseUnitPrice: number
  listUnit: number
  productOff: number
  orgOff: number
  lineTotal: number
  productDiscountTotal: number
  orgDiscountTotal: number
  snapshotPrice?: number | null
  priceChanged?: boolean
  soldOut?: boolean
  stockShort?: boolean
  minOrder: number
  orderStep: number
  maxOrder: number
}

/** 상품 + 옵션 조합 하나를 가리키는 키 */
export function cartKey(productId: string, optionName?: string): string {
  return optionName ? `${productId}::${optionName}` : productId
}

const linesState = ref<CartLine[]>([])
const discountPctState = ref(0)
const loading = ref(false)
const loaded = ref(false)
let loadPromise: Promise<void> | null = null

function toCartLine(item: ServerCartItem): CartLine {
  const minOrder = Math.max(1, Number(item.minOrder ?? item.product.minOrder) || 1)
  const orderStep = normalizeOrderStep(item.orderStep ?? item.product.orderStep)
  const stock = Math.max(0, Number(item.product.stock) || 0)
  /* 주문 상한은 재고만. 최대 주문 필드는 쓰지 않는다. */
  const maxOrder = Math.max(minOrder, stock > 0 ? stock : minOrder)

  const salePrice = Number(item.product.price) || 0
  const listPrice = Number(item.product.listPrice) || salePrice
  const optionExtra = Number(item.optionExtra) || 0
  const lineDiscountPct =
    item.discountPct == null ? discountPctState.value : Math.max(0, Math.min(100, Number(item.discountPct) || 0))
  const priced = calcMallUnitPrice({
    listPrice,
    salePrice,
    optionExtra,
    orgDiscountPct: lineDiscountPct,
  })
  /* 서버 필드가 있으면 우선(0 포함), 없으면 동일 공식으로 보정 */
  const pickNum = (value: number | undefined | null, fallback: number) =>
    value == null || Number.isNaN(Number(value)) ? fallback : Number(value)
  const unitPrice = pickNum(item.unitPrice, priced.unitPrice)
  const baseUnitPrice = pickNum(item.baseUnitPrice, priced.catalogUnit)
  const listUnit = pickNum(item.listUnit, priced.listUnit)
  const productOff = pickNum(item.productOff, priced.productOff)
  const orgOff = pickNum(item.orgOff, priced.orgOff)
  const qty = item.qty

  const product = {
    id: item.product.id,
    name: item.product.name,
    category: item.product.category as MallProduct['category'],
    price: salePrice,
    listPrice,
    shippingFee: Math.max(0, Number(item.product.shippingFee) || 0),
    shippingCharge: normalizeShippingCharge(item.product.shippingCharge),
    shippingEvery: normalizeShippingEvery(item.product.shippingEvery),
    sellUnit: normalizeSellUnit(item.product.sellUnit),
    imageShows: normalizeImageShows(item.product.imageShows),
    packQty: normalizePackQty(item.product.packQty),
    image: item.product.image || item.product.thumbnail || '',
    unit: item.product.unit,
    stock: item.product.stock,
    storeName: item.product.storeName,
    minOrder,
    orderStep,
    maxOrder,
  } as MallProduct

  return {
    idx: item.idx,
    productId: item.productId,
    qty,
    optionName: item.optionName,
    optionExtra: optionExtra || undefined,
    key: item.key || cartKey(item.productId, item.optionName),
    product,
    unitPrice,
    baseUnitPrice,
    listUnit,
    productOff,
    orgOff,
    lineTotal: pickNum(item.lineTotal, unitPrice * qty),
    productDiscountTotal: pickNum(item.productDiscountTotal, productOff * qty),
    orgDiscountTotal: pickNum(item.orgDiscountTotal, orgOff * qty),
    snapshotPrice: item.snapshotPrice,
    priceChanged: Boolean(item.priceChanged),
    soldOut: Boolean(item.soldOut),
    stockShort: Boolean(item.stockShort),
    minOrder,
    orderStep,
    maxOrder,
  }
}

function applyCart(items: ServerCartItem[], discountPct?: number) {
  if (discountPct != null && Number.isFinite(Number(discountPct))) {
    discountPctState.value = Math.max(0, Math.min(100, Number(discountPct) || 0))
  } else if (items[0]?.discountPct != null) {
    discountPctState.value = Math.max(0, Math.min(100, Number(items[0].discountPct) || 0))
  }
  linesState.value = items.map(toCartLine)
  loaded.value = true
}

function readLegacyLocal(): CartItem[] {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (item): item is CartItem =>
          item && typeof item.productId === 'string' && typeof item.qty === 'number' && item.qty > 0,
      )
      .map((item) => ({
        productId: item.productId,
        qty: Math.max(1, Math.floor(item.qty)),
        optionName: typeof item.optionName === 'string' ? item.optionName : undefined,
        optionExtra: Number.isFinite(item.optionExtra) ? Number(item.optionExtra) : undefined,
      }))
  } catch {
    return []
  }
}

function clearLegacyLocal() {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/** 로그아웃 시 화면 상태만 비운다. 서버 장바구니는 회원 계정에 남는다. */
export function clearCartData() {
  linesState.value = []
  discountPctState.value = 0
  loaded.value = false
}

async function ensureLoaded(force = false) {
  if (loaded.value && !force) return
  if (loadPromise && !force) {
    await loadPromise
    return
  }

  loading.value = true
  loadPromise = (async () => {
    try {
      const legacy = readLegacyLocal()
      if (legacy.length) {
        const cart = await mergeServerCart(legacy)
        clearLegacyLocal()
        applyCart(cart.items, cart.discountPct)
        return
      }

      const cart = await fetchServerCart()
      applyCart(cart.items, cart.discountPct)
    } catch {
      /* 비로그인·세션 만료면 빈 장바구니로 둔다 */
      linesState.value = []
      discountPctState.value = 0
      loaded.value = true
    } finally {
      loading.value = false
      loadPromise = null
    }
  })()

  await loadPromise
}

export function useCart() {
  void ensureLoaded()

  const lines = computed(() => linesState.value)
  const itemCount = computed(() => linesState.value.reduce((sum, item) => sum + item.qty, 0))
  const productCount = computed(() => linesState.value.length)
  const subtotal = computed(() => linesState.value.reduce((sum, line) => sum + line.lineTotal, 0))
  const listSubtotal = computed(() =>
    linesState.value.reduce((sum, line) => sum + line.listUnit * line.qty, 0),
  )
  const productDiscountTotal = computed(() =>
    linesState.value.reduce((sum, line) => sum + line.productDiscountTotal, 0),
  )
  const orgDiscountTotal = computed(() =>
    linesState.value.reduce((sum, line) => sum + line.orgDiscountTotal, 0),
  )
  const discountPct = computed(() => discountPctState.value)
  const discountTotal = computed(() => Math.max(0, listSubtotal.value - subtotal.value))
  /* 주문 배송비 = 담은 상품 배송비 중 최댓값(0이면 무료). 금액 임계 무료배송은 쓰지 않는다. */
  const shippingFee = computed(() =>
    calcOrderShippingFromLines(
      linesState.value.map((line) => toShippingLine(line.product, line.qty)),
    ),
  )
  const payable = computed(() => subtotal.value + shippingFee.value)
  const isEmpty = computed(() => linesState.value.length === 0)

  const getQty = (productId: string) =>
    linesState.value.filter((item) => item.productId === productId).reduce((sum, item) => sum + item.qty, 0)

  const addItem = async (
    productId: string,
    qty = 1,
    option?: { name: string; extra: number },
    mode: 'add' | 'set' = 'add',
  ) => {
    if (!productId || qty < 1) return
    try {
      const cart = await addServerCartItem({
        productId,
        qty,
        optionName: option?.name,
        optionExtra: option?.extra,
        mode,
      })
      applyCart(cart.items, cart.discountPct)
    } catch (error) {
      throw error instanceof Error ? error : new Error('장바구니에 담지 못했습니다.')
    }
  }

  const setQty = async (key: string, qty: number) => {
    const line = linesState.value.find((item) => item.key === key)
    if (!line) return

    try {
      const cart =
        qty < 1
          ? await removeServerCartItem(line.idx)
          : await updateServerCartItem(line.idx, Math.max(1, Math.floor(qty)))
      applyCart(cart.items, cart.discountPct)
    } catch (error) {
      throw error instanceof Error ? error : new Error('수량을 바꾸지 못했습니다.')
    }
  }

  const setOption = async (key: string, option: { name: string; extra: number }) => {
    const line = linesState.value.find((item) => item.key === key)
    if (!line) return

    try {
      const cart = await updateServerCartItem(line.idx, {
        optionName: option.name,
        optionExtra: option.extra,
      })
      applyCart(cart.items, cart.discountPct)
    } catch (error) {
      throw error instanceof Error ? error : new Error('옵션을 바꾸지 못했습니다.')
    }
  }

  const removeItem = async (key: string) => {
    const line = linesState.value.find((item) => item.key === key)
    if (!line) return
    try {
      const cart = await removeServerCartItem(line.idx)
      applyCart(cart.items, cart.discountPct)
    } catch (error) {
      throw error instanceof Error ? error : new Error('항목을 삭제하지 못했습니다.')
    }
  }

  const clear = async () => {
    try {
      const cart = await clearServerCart()
      applyCart(cart.items, cart.discountPct)
    } catch {
      clearCartData()
    }
  }

  const reload = () => ensureLoaded(true)

  return {
    items: lines,
    lines,
    loading,
    loaded,
    itemCount,
    productCount,
    subtotal,
    listSubtotal,
    productDiscountTotal,
    orgDiscountTotal,
    discountPct,
    discountTotal,
    shippingFee,
    payable,
    isEmpty,
    getQty,
    addItem,
    setQty,
    setOption,
    removeItem,
    clear,
    reload,
  }
}
